import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowLeft, Mail, Phone } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { company } from "@/data/company";

export default function ThankYou() {
  const { state } = useLocation();
  const ref = state?.ref;
  const name = state?.name;

  useEffect(() => {
    document.title = "Thank You — NetForge";
    let el = document.head.querySelector('meta[name="robots"]');
    const prev = el?.getAttribute("content");
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, follow");
    return () => {
      if (prev) el.setAttribute("content", prev);
    };
  }, []);

  return (
    <div data-testid="thankyou-page" className="bg-[#0a0f1e] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-xl text-center"
        >
          <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#22d3ee]/10 border border-[#22d3ee]/30 rounded-full mb-8">
            <CheckCircle2 size={32} className="text-[#22d3ee]" />
          </div>

          <p className="text-xs uppercase tracking-[0.25em] text-[#22d3ee] font-bold mb-4">
            Inquiry received
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white leading-tight">
            Thank you{name ? `, ${name.split(" ")[0]}` : ""}.
          </h1>
          <p className="mt-6 text-slate-400 leading-relaxed">
            We&apos;ve received your message and sent a confirmation to your inbox.
            Our team will get back to you within <strong className="text-slate-200">48 hours</strong>.
          </p>

          {ref && (
            <div
              data-testid="thankyou-ref"
              className="mt-8 inline-flex flex-col items-center gap-1 bg-[#10182b] border border-white/10 rounded-sm px-8 py-5"
            >
              <span className="text-xs uppercase tracking-wider text-slate-500">
                Your reference number
              </span>
              <span className="font-heading text-2xl font-black text-[#22d3ee] tracking-wider">
                {ref}
              </span>
            </div>
          )}

          <p className="mt-6 text-sm text-slate-500">
            Please keep this reference number for future correspondence.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              data-testid="thankyou-home-button"
              className="inline-flex items-center gap-2 bg-[#22d3ee] text-[#0a0f1e] font-bold px-6 py-3 rounded-sm hover:bg-[#67e8f9] transition-colors"
            >
              <ArrowLeft size={16} /> Back to home
            </Link>
            <div className="flex items-center gap-5 text-sm text-slate-400">
              <a href={`mailto:${company.email}`} className="inline-flex items-center gap-2 hover:text-[#22d3ee] transition-colors">
                <Mail size={15} /> Email
              </a>
              <a href={`tel:${company.phone}`} className="inline-flex items-center gap-2 hover:text-[#22d3ee] transition-colors">
                <Phone size={15} /> {company.phone}
              </a>
            </div>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
